/* eslint-disable */
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, StyledEngineProvider } from '@mui/material';

// routing
import Routes from 'routes';

// defaultTheme
import themes from 'themes';

// project imports
import NavigationScroll from 'layout/NavigationScroll';
import ReactLoading from 'react-loading';
import { useAuth0 } from '@auth0/auth0-react';
import AppProvider from './components/AppProvider.js';
import { useIdleTimer } from './hooks/useIdleTimer.js';

// --- STYLES FOR POPUP & ANIMATION ---
const popupStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#fff',
    padding: '30px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    borderRadius: '12px',
    zIndex: 10000,
    textAlign: 'center',
    border: '2px solid #007bff',
    minWidth: '300px'
};

const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 9999
};

const buttonStyle = {
    marginTop: '20px',
    padding: '10px 25px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold'
};

const securityStyles = `
    .security-container { position: relative; display: flex; justify-content: center; align-items: center; }
    .shield-pulse { position: absolute; width: 100px; height: 100px; background-color: rgba(0, 123, 255, 0.1); border-radius: 50%; animation: pulse 2s infinite ease-out; }
    .lock-icon { position: relative; z-index: 2; animation: float 3s infinite ease-in-out; }
    .loading-bar { height: 4px; width: 100%; background-color: #e9ecef; border-radius: 2px; position: relative; overflow: hidden; }
    .loading-bar::after { content: ''; position: absolute; width: 40%; height: 100%; background-color: #007bff; animation: loadingMove 1.5s infinite linear; }
    @keyframes pulse { 0% { transform: scale(0.8); opacity: 0.8; } 100% { transform: scale(2); opacity: 0; } }
    @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
    @keyframes loadingMove { 0% { left: -40%; } 100% { left: 100%; } }
`;

const App = () => {
    const { isLoading, logout, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
    const customization = useSelector((state) => state.customization);

    // 1. IDLE TIMER LOGIC
    const { isWarningShown, countdown, resetTimer } = useIdleTimer({
        timeout: 900, // 15 minutes
        warningTime: 890, // Show popup only at the very end (last 10 seconds)
        onWarning: () => {
            if (isAuthenticated) console.log('Session expiring soon');
        },
        onTimeout: () => {
            if (isAuthenticated) logout({ returnTo: window.location.origin });
        }
    });

    // 2. CRYPTO UTILITIES
    async function getStoredPrivateKey() {
        return new Promise(async (resolve, reject) => {
            const DB_NAME = 'trustedDeviceDB';

            // 1. Check if the database exists at all before opening
            // This prevents the creation of an "empty" Version 1 shell
            if (window.indexedDB.databases) {
                const dbs = await window.indexedDB.databases();
                const exists = dbs.some((db) => db.name === DB_NAME);
                if (!exists) {
                    return reject(new Error('Device not trusted (No DB)'));
                }
            }

            // 2. Open the database (no version specified to use current)
            const request = window.indexedDB.open(DB_NAME);

            request.onsuccess = () => {
                const db = request.result;

                // 3. Safety Check: If store is missing, we aren't trusted
                if (!db.objectStoreNames.contains('keys')) {
                    db.close();
                    return reject(new Error('Device not trusted (No Store)'));
                }

                try {
                    const tx = db.transaction('keys', 'readonly');
                    const store = tx.objectStore('keys');
                    const getReq = store.get('deviceEntry');

                    getReq.onsuccess = () => {
                        if (getReq.result) {
                            resolve(getReq.result);
                        } else {
                            reject(new Error('Key entry missing'));
                        }
                    };

                    // CRITICAL: Always close the connection
                    tx.oncomplete = () => db.close();
                    tx.onerror = () => {
                        db.close();
                        reject(new Error('Read transaction failed'));
                    };
                } catch (err) {
                    db.close();
                    reject(err);
                }
            };

            request.onerror = () => reject(new Error('IndexedDB access denied'));
        });
    }

    async function generateDeviceSignature(challengeString) {
        try {
            //console.log('Generating device signature for challenge:', challengeString);
            const { privateKey, deviceName } = await getStoredPrivateKey();
            const encoder = new TextEncoder();
            const challengeBytes = encoder.encode(challengeString);

            // Sign directly with bytes to avoid double-encoding bug
            const signatureBuffer = await window.crypto.subtle.sign({ name: 'Ed25519' }, privateKey, challengeBytes);

            const rawBase64 = btoa(String.fromCharCode(...new Uint8Array(signatureBuffer)));
            const safeSignature = rawBase64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

            return { signature: safeSignature, deviceName };
        } catch (err) {
            console.error('Error generating device signature:', err);
        }
    }

    // 3. LOGIN INTERCEPTION (Pre-redirect logic)
    useEffect(() => {
        const prepareLogin = async () => {
            //console.log('Preparing secure login...');
            if (!isLoading && !isAuthenticated) {
                try {
                    //console.log('Attempting to generate device signature for secure login...');
                    const { signature, deviceName } = await generateDeviceSignature('auth0-login-challenge');

                    //console.log('"Signature' + signature + '" generated for device "' + deviceName + '". Proceeding with secure login.');
                    // Auth0 v1.12.0 Syntax (Flat object, no authorizationParams)
                    await loginWithRedirect({
                        'ext-device-id': deviceName,
                        'ext-device-proof': encodeURIComponent(signature)
                    });
                } catch (err) {
                    //console.log('Falling back to normal login:', err);
                    await loginWithRedirect();
                }
            }
        };
        prepareLogin();
    }, [isLoading, isAuthenticated, loginWithRedirect]);

    // --- RENDER LOGIC ---

    // A. Initial Loading State
    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <ReactLoading type="Spin" color="#007bff" />
            </div>
        );
    }

    // B. Unauthenticated State (The Security Animation)
    if (!isAuthenticated) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    backgroundColor: '#f8f9fa',
                    fontFamily: 'sans-serif'
                }}
            >
                <style>{securityStyles}</style>
                <div className="security-container">
                    <div className="shield-pulse"></div>
                    <div className="lock-icon">
                        <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M12 2C9.24 2 7 4.24 7 7V10H6C4.9 10 4 10.9 4 12V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V12C20 10.9 19.1 10 18 10H17V7C17 4.24 14.76 2 12 2ZM10 7C10 5.9 10.9 5 12 5C13.1 5 14 5.9 14 7V10H10V7ZM12 18C10.9 18 10 17.1 10 16C10 14.9 10.9 14 12 14C13.1 14 14 14.9 14 16C14 17.1 13.1 18 12 18Z"
                                fill="#007bff"
                            />
                        </svg>
                    </div>
                </div>
                <h2 style={{ marginTop: '30px', color: '#333', fontWeight: '600' }}>Securing Session</h2>
                <p style={{ color: '#6c757d', marginTop: '5px' }}>Verifying device integrity...</p>
                <div style={{ marginTop: '20px', width: '150px' }}>
                    <div className="loading-bar"></div>
                </div>
            </div>
        );
    }

    // C. Authenticated State (The Real App)
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={themes(customization)}>
                <AppProvider>
                    <CssBaseline />

                    {/* IDLE WARNING OVERLAY */}
                    {isWarningShown && (
                        <>
                            <div style={overlayStyle} />
                            <div style={popupStyle}>
                                <div style={{ fontSize: '30px', marginBottom: '10px' }}>⏳</div>
                                <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>Session Expiring</h3>
                                <p style={{ color: '#666' }}>Your session will expire in:</p>
                                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545', margin: '10px 0' }}>{countdown}s</div>
                                <button
                                    style={buttonStyle}
                                    onClick={async () => {
                                        try {
                                            await getAccessTokenSilently();
                                            resetTimer();
                                        } catch (err) {
                                            logout({ returnTo: window.location.origin });
                                        }
                                    }}
                                >
                                    STAY LOGGED IN
                                </button>
                            </div>
                        </>
                    )}

                    <NavigationScroll>
                        <Routes />
                    </NavigationScroll>
                </AppProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default App;
